import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, ChevronRight, Clock, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { useConfirm } from '../ConfirmDialog';
import { api, eur, frDate, errMsg, StatutBadge, AFFECTATION, PREP } from './api';
import { DocumentViewer } from './DocumentViewer';
import { InvoiceFields, editable, toPayload } from './InvoiceFields';
import { VehiclePicker } from './VehiclePicker';
import { VehicleFraisList } from './VehicleFraisList';
import { DuplicatesAlert } from './DuplicatesAlert';
import { SplitDialog } from './SplitDialog';
import { SplitDraftBanner, SplitSummary } from './SplitSummary';
import { InvoiceActions, makeHandlers } from './InvoiceActions';
import { AssignPanel, InvoiceHistory } from './AssignPanel';
import { PreparationBanner } from './PreparationBanner';
import { MonthExpenses } from './MonthExpenses';
import { useAuth } from '../../context/AuthContext';
import { InvoiceRowMenu } from './InvoiceCleanup';
import { InvoiceLinesPanel } from './InvoiceLinesPanel';
import { LinkExistingDialog } from './LinkExistingDialog';
import { CorrectionBox } from './CorrectionBox';

const MONEY = ['montant_ht', 'montant_tva', 'montant_ttc'];

export function InvoiceDetail({ id, rev, cleanup, onDone, onRefresh, onNext, onBack, onOpen }) {
    const [d, setD] = useState(null);
    const [v, setV] = useState({});
    const [vehicle, setVehicle] = useState(null);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState('');
    const [split, setSplit] = useState(false);
    const [link, setLink] = useState(null);
    const [ver, setVer] = useState(0);
    const [confirm, confirmDialog] = useConfirm();
    const { user } = useAuth();
    const canValidate = user?.role !== 'assistant' || !!user?.peut_valider;
    PREP.on = !canValidate;
    const load = useCallback(() => api.detail(id).then(x => { setD(x); setV(Object.fromEntries(editable.map(k => [k, MONEY.includes(k) && x[k] !== null && x[k] !== undefined && x[k] !== '' ? Number(x[k]).toFixed(2) : (x[k] ?? '')]))); setVehicle(x.vehicule); setNote(x.note_controle || ''); setVer(n => n + 1); }).catch(e => toast.error(errMsg(e))), [id]);
    useEffect(() => { setD(null); load(); }, [load]);
    useEffect(() => { if (rev) load(); }, [rev]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!d) return <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>;

    const pickVehicle = async (pv) => {
        try { await api.attribute([d.id], pv.id); setVehicle(await api.vehicle(pv.id, d.id)); toast.success(`Attribuée à ${pv.immatriculation} — brouillon enregistré, aucune dépense créée`); setVer(n => n + 1); onRefresh?.(); }
        catch (e) { toast.error(errMsg(e)); }
    };
    const clearVehicle = async () => {
        try { await api.attribute([d.id], null); setVehicle(null); toast.success('Attribution retirée — rien n\'était comptabilisé'); setVer(n => n + 1); onRefresh?.(); } catch (e) { toast.error(errMsg(e)); }
    };
    const saveOnly = async () => {
        setBusy(true);
        try { await persist(); await api.note(d.id, note); toast.success('Modifications enregistrées — facture toujours en attente'); await load(); onRefresh?.(); }
        catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    };
    const keepPlate = async (plate) => {
        setBusy(true);
        try { await api.keepPlate(d.id, plate); toast.success(`Plaque ${plate} enregistrée — facture laissée en attente, aucune dépense créée`); await load(); onRefresh?.(); }
        catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    };
    const plateProps = { rid: d.id, pendingPlate: d.immatriculation_detectee, onKeepPlate: keepPlate, busy };
    const persist = async () => {
        const body = toPayload(v);
        if (vehicle && vehicle.id !== d.vehicule?.id) body.vehicle_match = { matched: true, vehicle_id: vehicle.id, immatriculation: vehicle.immatriculation, nom: vehicle.nom, modele: vehicle.modele, commercial: vehicle.commercial };
        await api.save(d.id, body);
    };
    const run = async (fn, okMsg) => {
        setBusy(true);
        try { const r = await fn(); if (r !== false) { toast.success(r?.status === 'prepare' ? 'Préparation envoyée — un gérant la validera' : r?.status === 'justificatif_doublon' ? 'Conservée comme justificatif — aucun nouveau coût' : okMsg); onDone(d.id); } }
        catch (e) { toast.error(errMsg(e)); load(); }
        finally { setBusy(false); }
    };
    const nDup = (d.doublons || []).filter(x => x.niveau !== 'recurrente').length;
    const dupWarn = nDup ? `\n\nAttention : ${nDup} doublon(s) possible(s) détecté(s).` : '';
    const h = makeHandlers({ d, v, vehicle, note, confirm, persist, run, dupWarn, setSplit, setLink });

    return (
        <div className="h-full flex flex-col lg:flex-row gap-4 overflow-auto lg:overflow-visible" data-testid="invoice-detail">
            {confirmDialog}
            {onBack && <button onClick={onBack} className="lg:hidden flex items-center gap-1 text-[13px] text-gray-500 shrink-0" data-testid="invoice-back"><ArrowLeft className="w-4 h-4" />Liste</button>}
            <div className="lg:w-1/2 h-[45vh] lg:h-full shrink-0">
                <DocumentViewer doc={d.document} />
            </div>
            <div className="lg:w-1/2 lg:min-h-0 lg:overflow-auto space-y-4 pb-6">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <StatutBadge s={d.statut} a={d.affectation?.type} testId="invoice-detail-statut" />
                        <span className="text-[11px] text-gray-400 truncate">Scannée le {frDate(d.document?.scanne_le || d.created_at)}{d.document?.filename ? ` · ${d.document.filename}` : ''}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        {cleanup && <InvoiceRowMenu it={d} c={cleanup} testId="invoice-detail-menu" />}
                        <Button variant="ghost" size="sm" onClick={onNext} className="text-[12px]" data-testid="invoice-next">Suivante<ChevronRight className="w-4 h-4" /></Button>
                    </div>
                </div>
                {d.ecart && <p className="text-[12px] rounded-lg bg-gray-100 text-gray-700 px-3 py-2" data-testid="invoice-ecart-info">Écartée le {frDate(d.ecart.le)} par {d.ecart.par} — motif : <b>{d.ecart.motif_label}</b>{d.ecart.commentaire ? ` (${d.ecart.commentaire})` : ''}</p>}
                <InvoiceFields v={v} set={setV} disabled={!d.traitable} />
                <DuplicatesAlert items={d.doublons} onOpen={onOpen} />
                {d.traitable && <MonthExpenses mois={(v.date_facture || '').slice(0, 7)} ttc={v.montant_ttc} ht={v.montant_ht} />}
                <InvoiceLinesPanel d={d} ver={ver} onOpen={onOpen} confirm={confirm} onSplit={() => setSplit(true)} setLink={setLink} onChanged={() => { load(); onRefresh?.(); }} />
                {d.autres_lignes_document?.some(o => o.groupe !== d.groupe) && (
                    <div className="text-[12px] text-gray-600" data-testid="invoice-other-lines">
                        <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-1">Autres factures du même document scanné</p>
                        {d.autres_lignes_document.filter(o => o.groupe !== d.groupe).map(o => <button key={o.id} onClick={() => onOpen(o.id)} className="block text-left hover:text-[#6366F1]">{o.fournisseur} · {eur(o.montant_ttc)} · {o.vehicle_match?.immatriculation || o.immatriculation_detectee || '—'} · <StatutBadge s={o.statut} a={o.affectation_type} /></button>)}
                    </div>
                )}
                {d.traitable && d.repartition && !d.preparation && <SplitDraftBanner r={d.repartition} onOpen={() => setSplit(true)} />}
                {d.traitable && <PreparationBanner d={d} h={h} run={run} confirm={confirm} busy={busy} canValidate={canValidate} />}
                {!canValidate && d.traitable && <p className="text-[11px] text-sky-700" data-testid="invoice-prep-mode">Mode préparation : vos choix sont envoyés à un gérant pour validation, rien n'est enregistré en comptabilité.</p>}
                {d.traitable ? (
                    <>
                        <AssignPanel d={d} vehicle={vehicle} plateProps={plateProps} busy={busy} run={run} persist={persist} confirm={confirm} onSplit={() => setSplit(true)} vehicleTab={(
                            <div className="space-y-3">
                                <VehiclePicker vehicle={vehicle} onPick={pickVehicle} onClear={clearVehicle} {...plateProps} />
                                <VehicleFraisList vehicle={vehicle} canAct busy={busy} onLink={h.linkFrais} />
                                <InvoiceActions d={d} h={h} busy={busy} hasVehicle={!!vehicle} />
                            </div>
                        )} />
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Note de contrôle (facultatif)" className="text-[12px] min-h-[44px] flex-1" data-testid="invoice-note" />
                            <Button variant="outline" onClick={saveOnly} disabled={busy} className="sm:self-stretch" data-testid="invoice-save"><Save className="w-4 h-4 mr-1.5" />Enregistrer</Button>
                            <Button variant="outline" onClick={h.wait} disabled={busy} className="sm:self-stretch" data-testid="invoice-wait"><Clock className="w-4 h-4 mr-1.5" />Laisser en attente</Button>
                        </div>
                    </>
                ) : (
                    <>
                        {vehicle && <VehicleFraisList vehicle={vehicle} canAct={false} />}
                        {d.affectation?.type === 'repartition' && <SplitSummary a={d.affectation} />}
                        {d.affectation && <p className="text-[12px] text-gray-700" data-testid="invoice-affectation">Affectée : <b>{AFFECTATION[d.affectation.type] || d.affectation.type}</b> par {d.affectation.par} le {frDate(d.affectation.le)}</p>}
                        <InvoiceActions d={d} h={h} busy={busy} hasVehicle={!!vehicle} />
                        <CorrectionBox d={d} onDone={() => { load(); onRefresh?.(); }} />
                    </>
                )}
                <InvoiceHistory key={ver} id={d.id} />
            </div>
            <LinkExistingDialog link={link} run={run} onClose={() => setLink(null)} />
            {split && <SplitDialog open={split} invoice={d} run={run} confirm={confirm} onClose={(refresh) => { setSplit(false); if (refresh) { load(); onRefresh?.(); } }} />}
        </div>
    );
}
